/**
 * @author NikolaV
 */
import { Ajax } from '@shared/Ajax';
import Feature, { FeatureLike } from 'ol/Feature';
import { latLonToEN } from '../ol-utils';

import { FeatureMouseInteraction } from '@openlayers/interaction/FeatureMouseInteraction';
import { MultiLineString as OlMultiLineString, Point as OlPoint } from 'ol/geom';
import * as olStyle from 'ol/style';

import { VehiclesService } from '@shared/vehicles/VehiclesService';


const FEATURE_TYPE = 'vehicle';
const FEATURE_TAIL_ID = 'vehicle-tail';

const iconStylesCache = {};

const styles = {
    circle: new olStyle.Style({ // for debug, draw feature center to offset icon
        image: new olStyle.Circle({
            fill: new olStyle.Fill({
                color: [0, 0, 0, 1],
            }),
            radius: 2,
        }),
    }),

    tail: new olStyle.Style({
        stroke: new olStyle.Stroke({
            color: '#359100',
            width: 2,
            lineDash: [10, 4],
        }),
    }),
};

function styleFunction(feature: FeatureLike, resolution: number) {
    const vehicle: IVehicle = feature.get('data');
    const isXmas = new Date().getMonth() === 11;
    return createStyle(vehicle, resolution < 6, isXmas);
}

function createStyle(vehicle: IVehicle, showText: boolean = true, xmas = false) {
    const cacheKey = (showText ? vehicle.id : vehicle.type) + '_' + vehicle.reportAlert;
    if (iconStylesCache[cacheKey]) return iconStylesCache[cacheKey];

    try {
        const def = VehiclesService.TYPES[vehicle.type];
        const iconSrc = vehicle.reportAlert ? def.iconGrayscale : def.icon;
        const offsetX = 12;
        const offsetY = 50;

        iconStylesCache[cacheKey] = [
            new olStyle.Style({
                image: new olStyle.Icon({
                    src: iconSrc,
                    anchorXUnits: 'pixels',
                    anchorYUnits: 'pixels',
                    scale: 0.6,
                    anchor: [offsetX, offsetY + 25],
                }),
                text: showText ? new olStyle.Text({
                    text: VehiclesService.VendorNames[vehicle.vendor],
                    offsetY: offsetY - 50,
                    offsetX: offsetX,
                    textAlign: 'left',
                    font: '11px Calibri',
                    fill: new olStyle.Fill({ color: '#ffffff' }),
                    backgroundFill: new olStyle.Fill({ color: 'black' }),
                    padding: [1, 3, 1, 3]
                }) : null
            }),
            showText ? new olStyle.Style({
                text: new olStyle.Text({
                    text: vehicle.name,
                    offsetY: offsetY - 35,
                    offsetX: -10,
                    textAlign: 'left',
                    font: '11px Calibri',
                    fill: new olStyle.Fill({ color: '#ffffff' }),
                    backgroundFill: new olStyle.Fill({ color: '#808080' }),
                    padding: [1, 3, 1, 3]
                })
            }) : null,
            styles.circle
        ].filter(Boolean);

        return iconStylesCache[cacheKey];
    } catch (e) {
        console.error('VehiclesFeatureFactory.createIconStyle()', vehicle);
        console.error('error', e);
        return;
    }
}

function makeFeature(vehicle: IVehicle, mouseInteraction?: FeatureMouseInteraction): Feature {
    if (vehicle.onHold) {
        return null;
    }
    if (!vehicle.position || !vehicle.position.latitude || !vehicle.position.longitude) {
        console.error('VehiclesFeatureFactory.makeFeature() - skipping vehicle, no coordinates: ', vehicle);
        return null;
    }
    const coord: Coordinate = latLonToEN(vehicle.position.latitude, vehicle.position.longitude);
    if (!coord) {
        console.error('VehiclesFeatureFactory.makeFeature() - skipping vehicle, bad coordinates: ', vehicle);
        return null;
    }

    const feature = new Feature({
        geometry: new OlPoint(coord),
    });
    feature.set('data', vehicle);
    feature.set('type', FEATURE_TYPE);
    feature.set('mouseInteraction', mouseInteraction);
    feature.setId(vehicle.id);

    return feature;
}

function makeTailFeature(routes): Feature {
    if (!routes) {
        console.error('VehiclesFeatureFactory.makeTailFeature() - routes undefined ', routes);
        return;
    }

    if (routes.length === 0) {
        console.log('VehiclesFeatureFactory.makeTailFeature() - routes object empty: ', routes);
        return;
    }

    const routesCollection = [];

    routes.forEach(route => {
        const tailCoords = route.map(pos => latLonToEN(pos.latitude, pos.longitude));
        routesCollection.push(tailCoords);
    });

    const feature = new Feature({
        geometry: new OlMultiLineString(routesCollection),
    });

    feature.setStyle(styles.tail);
    feature.setId(FEATURE_TAIL_ID);

    return feature;
}

function fetchAndMakeTailFeature(vehicleId: number, from?: number, to?: number) {
    return Ajax({
        url: 'ajax_vehicle_positiontail.json',
        data: {
            vehicleId,
            sinceUnixTimeMillis: from,
            untilUnixTimeMillis: to
        },
    }).then(resp => ({
        resultTruncated: resp.resultTruncated,
        feature: makeTailFeature(resp.routes),
        routes: resp.routes
    }));
}

export const VehiclesFeatureFactory = {
    makeFeature,

    makeTailFeature,
    fetchAndMakeTailFeature,

    styleFunction,
    styles,

    FEATURE_TYPE
};
